import moment from 'moment';
import React, { Fragment } from 'react';
import * as styles from './NotificationContent.module.scss';
import NotificationItem from './NotificationItem';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { NotificationType } from '../graphql/generated/graphql';
import notificationsImage from '../images/empty/notifications.svg';
import groupByCutoffs from '../utils/groupByCutoffs';

const NOTIFICATIONS_FRAGMENT = gql(`
  fragment NotificationContent_notifications on Notification {
    id
    type
    actor {
      id
    }
    ... on CommentNotification {
      # eslint-disable-next-line @graphql-eslint/require-selections
      comment {
        thread {
          id
          post {
            id
          }
        }
      }
    }
    ... on HistoryNotification {
      # eslint-disable-next-line @graphql-eslint/require-selections
      history {
        post {
          id
        }
      }
    }
    # eslint-disable-next-line @graphql-eslint/require-selections
    ...NotificationItem_notifications
  }
`);

type NotificationsFragment = DocumentType<typeof NOTIFICATIONS_FRAGMENT>;

type NotEmptyArray<T> = { 0: T } & Array<T>;

const isInGroup = (
  [n1]: NotEmptyArray<NotificationsFragment>,
  n2: NotificationsFragment,
) => {
  if (n1.type !== n2.type || n1.actor?.id !== n2.actor?.id) {
    return false;
  }

  if ('comment' in n1 && 'comment' in n2 && n1.comment && n2.comment) {
    if (n1.type === NotificationType.CommentReply) {
      return n1.comment.thread.id === n2.comment.thread.id;
    }
    return n1.comment.thread.post.id === n2.comment.thread.post.id;
  }

  if (
    n1.type === NotificationType.PostEdit &&
    'history' in n1 &&
    'history' in n2 &&
    n1.history &&
    n2.history
  ) {
    return n1.history.post.id === n2.history.post.id;
  }
  return false;
};

export const groupNotifications = (notifications: NotificationsFragment[]) =>
  notifications.reduce<NotEmptyArray<NotificationsFragment>[]>(
    (groups, notification) => {
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && isInGroup(lastGroup, notification)) {
        lastGroup.push(notification);
      } else {
        groups.push([notification]);
      }

      return groups;
    },
    [],
  );

interface NotificationContentProps {
  fetchMore: (() => void) | null;
  onNavigate?: () => void;
  notifications: NotificationsFragment[];
}

const NotificationContent = ({
  fetchMore,
  notifications,
  onNavigate,
}: NotificationContentProps) => {
  const cutoffs = [
    {
      key: 'today',
      label: null,
      cutoff: moment().startOf('day'),
    },
    {
      key: 'yesterday',
      label: 'Yesterday',
      cutoff: moment().subtract(1, 'day').startOf('day'),
    },
    {
      key: 'last_week',
      label: 'Last week',
      cutoff: moment().subtract(7, 'days').startOf('day'),
    },
    { key: 'older', label: 'Older' },
  ];
  let content;

  if (notifications.length === 0 && !fetchMore) {
    content = (
      <div className={styles.emptyState}>
        <img src={notificationsImage} alt="" />
        You&apos;re all caught up!
      </div>
    );
  } else {
    content = groupByCutoffs(cutoffs, notifications, 'insertedAt').map(
      ({ items, key, label }) => (
        <Fragment key={key}>
          {label && <div className={styles.sectionHeader}>{label}</div>}
          {groupNotifications(items).map((group) => (
            <NotificationItem
              key={group[0].id}
              notifications={group}
              onNavigate={onNavigate}
            />
          ))}
        </Fragment>
      ),
    );
  }

  return (
    <div>
      {content}
      <InfiniteScrollWaypoint fetchMore={fetchMore} />
    </div>
  );
};

export default NotificationContent;
